import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useScopedStorageKey } from "@/workspaces"
import { Button, Input, Skeleton, Tabs, TabsList, TabsTrigger } from "@coval/ui"
import { Play, Plus, Search, Square, X } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { useActiveItemAutoScroll } from "@/hooks/useActiveItemAutoScroll"
import { useCollectionPageShortcuts } from "@/hooks/useCollectionPageShortcuts"
import { useNavigationShortcuts } from "@/hooks/useNavigationShortcuts"

import { hashMetricMetadata } from "../../utils/experimentStorageKey"
import {
  TEST_DRAWER_TAB_OPTIONS,
  useTestDrawer,
  useTestMetricRuntime,
  type ViewMode,
} from "../contexts"
import { useCanRunEvaluations } from "../hooks/useCanRunEvaluations"
import {
  useRecentTranscriptsTransport,
  useSimulationOutputLookupTransport,
} from "../routes/useRecentTranscriptsTransport"
import { resolveRunSelection } from "../runSelection"
import {
  PASSING_VALUES,
  useBackendExperimentRunner,
} from "../useBackendExperimentRunner"
import { ANY_RESULT, MetricValueFilter } from "./MetricValueFilter"
import { RecentDetailView } from "./RecentDetailView"
import {
  RecentSimulationRow,
  type RecentSimulationItem,
  type TestResult,
} from "./RecentSimulationRow"

const log = logger("RecentTranscriptsView")

interface RecentTranscriptsViewProps {
  showTabs?: boolean
  headerContent?: React.ReactNode
  headerTitle?: string
  scopeContent?: React.ReactNode
  tabOptions?: typeof TEST_DRAWER_TAB_OPTIONS
  allowImport?: boolean
  /** true = monitoring data (default), false = simulation/run data */
  monitoringFilter?: boolean
}

export const RecentTranscriptsView: React.FC<RecentTranscriptsViewProps> = ({
  showTabs = true,
  headerContent,
  headerTitle = "Simulations",
  scopeContent,
  tabOptions = TEST_DRAWER_TAB_OPTIONS,
  allowImport = true,
  monitoringFilter = true,
}) => {
  const {
    metric,
    isNewMetric,
    simulationIds: scopedSimulationIds,
    beforeRun,
    criteriaOverride,
    experimentStorageScope,
    onRunningChange,
  } = useTestMetricRuntime()
  const { trackEvent } = useAnalytics()
  const canRunEvaluations = useCanRunEvaluations()
  const {
    viewMode,
    setViewMode,
    selectedSimulationId,
    handleRowClick,
    handleClose,
  } = useTestDrawer()

  // Storage key for persisting test results, scoped by org + workspace
  // (metric?.id falls back to a shared "new" until the metric is saved).
  const rawStorageKey = useMemo(() => {
    const baseKey = `metric-test-results-${metric?.id || "new"}`
    return experimentStorageScope === undefined
      ? baseKey
      : `${baseKey}-${hashMetricMetadata(experimentStorageScope)}`
  }, [experimentStorageScope, metric?.id])
  const storageKey = useScopedStorageKey(rawStorageKey)

  // Load persisted test results on mount
  const [testResults, setTestResults] = useState<Record<string, TestResult>>(
    () => {
      if (typeof window === "undefined" || !metric?.id || !storageKey) {
        return {}
      }
      try {
        const stored = sessionStorage.getItem(storageKey)
        if (stored) {
          return JSON.parse(stored) as Record<string, TestResult>
        }
      } catch (error) {
        log.error("Failed to load persisted test results", error)
      }
      return {}
    },
  )

  // Save test results to sessionStorage whenever they change
  useEffect(() => {
    if (typeof window === "undefined" || !metric?.id || !storageKey) {
      return
    }
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(testResults))
    } catch (error) {
      log.error("Failed to persist test results", error)
    }
  }, [testResults, storageKey, metric?.id])

  const [importedSimulations, setImportedSimulations] = useState<
    RecentSimulationItem[]
  >([])
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set())
  const [importId, setImportId] = useState("")
  const [importError, setImportError] = useState<string | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const importInputRef = useRef<HTMLInputElement | null>(null)
  const seededSimulationIdRef = useRef<string | null>(null)

  // Metric value filter state — scoped to the current metric only
  const metricOutputType = metric?.output_type ?? "string"
  const isFloatMetric = metricOutputType === "float"
  const [filterValue, setFilterValue] = useState<string>("")
  const [filterOperator, setFilterOperator] = useState<
    ">" | "<" | "=" | ">=" | "<="
  >("=")

  const isAnyResult = filterValue === ANY_RESULT

  const activeMetricValueFilters = useMemo(() => {
    if (!filterValue || !metric?.id || isAnyResult) {
      return undefined
    }
    return [
      {
        metricId: metric.id,
        operator: isFloatMetric ? filterOperator : ("=" as const),
        value: filterValue,
        valueType: isFloatMetric ? ("float" as const) : ("string" as const),
      },
    ]
  }, [metric?.id, filterValue, filterOperator, isFloatMetric, isAnyResult])

  const simulationOutputLookup = useSimulationOutputLookupTransport()

  const {
    data: simulationsData,
    isError: isSimulationsError,
    isLoading: isLoadingSimulations,
  } = useRecentTranscriptsTransport({
    activeMetricValueFilters,
    includeAnyResultMetric: isAnyResult,
    metricId: metric?.id,
    monitoringFilter,
  })

  // Combine fetched and imported simulations
  const simulations: RecentSimulationItem[] = useMemo(() => {
    const metricId = metric?.id
    const scopedIds = scopedSimulationIds ? new Set(scopedSimulationIds) : null
    const fetchedSimulations: RecentSimulationItem[] = simulationsData?.items
      ? simulationsData.items
          .filter(
            (item): item is typeof item & { simulation_output_id: string } =>
              item.simulation_output_id !== null,
          )
          .filter((item) => !excludedIds.has(item.simulation_output_id))
          .filter(
            (item) => !scopedIds || scopedIds.has(item.simulation_output_id),
          )
          .map((item) => ({
            id: item.simulation_output_id,
            name: item.simulation_output_id.slice(0, 8),
            description: item.agent_id || "Unknown agent",
            createdAt: item.created_at,
            agentId: item.agent_id ?? undefined,
            isImported: false,
            metricValue:
              metricId && item.metricsById
                ? item.metricsById[metricId]
                : undefined,
          }))
      : []

    const fetchedIds = new Set(fetchedSimulations.map((s) => s.id))
    const uniqueImported = importedSimulations.filter(
      (s) =>
        !fetchedIds.has(s.id) &&
        !excludedIds.has(s.id) &&
        (!scopedIds || scopedIds.has(s.id)),
    )

    const knownIds = new Set([
      ...fetchedIds,
      ...uniqueImported.map(({ id }) => id),
    ])
    const scopedPlaceholders: RecentSimulationItem[] = (
      scopedSimulationIds ?? []
    )
      .filter((id) => !knownIds.has(id) && !excludedIds.has(id))
      .map((id) => ({
        id,
        name: id.slice(0, 8),
        description: "Test case simulation",
        createdAt: "",
        isImported: false,
      }))

    return [...uniqueImported, ...fetchedSimulations, ...scopedPlaceholders]
  }, [
    simulationsData,
    importedSimulations,
    excludedIds,
    metric?.id,
    scopedSimulationIds,
  ])

  useEffect(() => {
    if (!selectedSimulationId) {
      return
    }
    if (seededSimulationIdRef.current === selectedSimulationId) {
      return
    }
    if (
      excludedIds.has(selectedSimulationId) ||
      simulations.some((s) => s.id === selectedSimulationId)
    ) {
      return
    }
    seededSimulationIdRef.current = selectedSimulationId
    let cancelled = false
    void simulationOutputLookup
      .getSimulationOutput(selectedSimulationId)
      .then((simData) => {
        if (cancelled || !simData) {
          return
        }
        setImportedSimulations((prev) =>
          prev.some((s) => s.id === selectedSimulationId)
            ? prev
            : [
                {
                  id: selectedSimulationId,
                  name: selectedSimulationId.slice(0, 8),
                  description: "Linked conversation",
                  createdAt: new Date().toISOString(),
                  isImported: true,
                },
                ...prev,
              ],
        )
      })
      .catch(() => null)
    return () => {
      cancelled = true
    }
  }, [selectedSimulationId, simulations, excludedIds, simulationOutputLookup])

  // Derive unique sorted string options from the fetched data for the metric filter
  const stringValueOptions = useMemo(() => {
    if (isFloatMetric || !metric?.id) {
      return []
    }
    const seen = new Set<string>()
    for (const item of simulationsData?.items ?? []) {
      const val = item.metricsById ? item.metricsById[metric.id] : undefined
      if (typeof val === "string" && val) {
        seen.add(val)
      }
    }
    return Array.from(seen).sort()
  }, [simulationsData, metric?.id, isFloatMetric])

  // Calculate pass rate for test results
  const passRate = useMemo(() => {
    const results = Object.values(testResults)
    if (results.length === 0) {
      return null
    }

    const passCount = results.filter(
      (r) => r.value != null && PASSING_VALUES.has(r.value),
    ).length

    return Math.round((passCount / results.length) * 100)
  }, [testResults])

  const runSimulationIds = useMemo(
    () =>
      resolveRunSelection(
        simulations.map((simulation) => simulation.id),
        null,
      ).runIds,
    [simulations],
  )

  const handleResult = useCallback((simId: string, result: TestResult) => {
    setTestResults((prev) => ({ ...prev, [simId]: result }))
  }, [])

  const {
    runExperiment: handleRunAll,
    runSingleExperiment: runTest,
    stopExperiment: handleStop,
    isRunning: isRunningAll,
    evaluatingIds,
  } = useBackendExperimentRunner({
    metric,
    onResult: handleResult,
    onComplete: useCallback(() => {}, []),
    simulationIds: runSimulationIds,
    beforeRun,
    criteriaCount: criteriaOverride?.length,
  })

  useEffect(() => {
    onRunningChange?.(isRunningAll)
    return () => onRunningChange?.(false)
  }, [isRunningAll, onRunningChange])

  // Import a simulation by ID
  const handleImport = useCallback(async () => {
    if (!importId.trim()) {
      return
    }
    setImportError(null)

    const simulationId = importId.trim()

    if (simulations.some((s) => s.id === simulationId)) {
      setImportError("Simulation already in list")
      return
    }

    if (excludedIds.has(simulationId)) {
      setExcludedIds((prev) => {
        const next = new Set(prev)
        next.delete(simulationId)
        return next
      })
      setImportId("")
      return
    }

    setIsImporting(true)
    try {
      const simData = await simulationOutputLookup
        .getSimulationOutput(simulationId)
        .catch(() => null)

      if (!simData) {
        setImportError("Simulation not found or has no transcript")
        return
      }

      const newSimulation: RecentSimulationItem = {
        id: simulationId,
        name: simulationId.slice(0, 8),
        description: "Imported",
        createdAt: new Date().toISOString(),
        isImported: true,
      }

      setImportedSimulations((prev) => [newSimulation, ...prev])
      setImportId("")
    } finally {
      setIsImporting(false)
    }
  }, [importId, simulations, excludedIds, simulationOutputLookup])

  // Delete a simulation from the list
  const handleDelete = useCallback(
    (simulationId: string) => {
      const isImported = importedSimulations.some((s) => s.id === simulationId)

      if (isImported) {
        setImportedSimulations((prev) =>
          prev.filter((s) => s.id !== simulationId),
        )
      } else {
        setExcludedIds((prev) => new Set(prev).add(simulationId))
      }

      setTestResults((prev) => {
        const next = { ...prev }
        delete next[simulationId]
        return next
      })
    },
    [importedSimulations],
  )

  const handleImportKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      void handleImport()
    }
  }

  // Navigation for detail view
  const currentIndex = useMemo(() => {
    if (!selectedSimulationId) {
      return -1
    }
    return simulations.findIndex((s) => s.id === selectedSimulationId)
  }, [selectedSimulationId, simulations])

  const handleNextSimulation = useCallback(() => {
    if (currentIndex < simulations.length - 1) {
      handleRowClick(simulations[currentIndex + 1].id)
    }
  }, [currentIndex, simulations, handleRowClick])

  const handlePreviousSimulation = useCallback(() => {
    if (currentIndex > 0) {
      handleRowClick(simulations[currentIndex - 1].id)
    }
  }, [currentIndex, simulations, handleRowClick])

  const handleRunCurrentTest = useCallback(() => {
    if (selectedSimulationId) {
      void runTest(selectedSimulationId)
    }
  }, [selectedSimulationId, runTest])

  const getSimulationId = useCallback(
    (simulation: RecentSimulationItem) => simulation.id,
    [],
  )

  const handleOpenSimulation = useCallback(
    (simulation: RecentSimulationItem) => {
      handleRowClick(simulation.id)
    },
    [handleRowClick],
  )

  useNavigationShortcuts({
    enabled: !!selectedSimulationId,
    includeHorizontalArrows: true,
    nextLabel: "Next simulated conversation",
    onNext:
      currentIndex >= 0 && currentIndex < simulations.length - 1
        ? handleNextSimulation
        : undefined,
    onPrevious: currentIndex > 0 ? handlePreviousSimulation : undefined,
    previousLabel: "Previous simulated conversation",
  })

  const {
    handlePointerEnter,
    handlePointerLeave,
    isActiveItem,
    keyboardActiveItemId,
  } = useCollectionPageShortcuts({
    enabled: !selectedSimulationId,
    getItemId: getSimulationId,
    itemLabel: "simulated conversation",
    items: simulations,
    onOpenItem: handleOpenSimulation,
    searchInputRef: importInputRef,
    searchLabel: "Search simulated conversations",
    searchValue: importId,
  })

  useActiveItemAutoScroll({
    activeItemId: keyboardActiveItemId,
    selectorAttribute: "data-recent-simulation-id",
  })

  // Show detail view if a simulation is selected
  if (selectedSimulationId) {
    return (
      <RecentDetailView
        testResult={testResults[selectedSimulationId] || null}
        isEvaluating={evaluatingIds.has(selectedSimulationId)}
        currentIndex={currentIndex}
        totalCount={simulations.length}
        onNext={handleNextSimulation}
        onPrevious={handlePreviousSimulation}
        onRunTest={handleRunCurrentTest}
      />
    )
  }

  const metricDisplayName =
    metric?.metric_display_name || metric?.metric_name || "metric"

  return (
    <>
      {/* Header */}
      <div className="px-4 py-4 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <h2 className="shrink-0 text-base font-medium text-coval-text">
            {headerTitle}
          </h2>
          {headerContent}
        </div>
        <div className="flex items-center gap-2">
          {canRunEvaluations && (
            <>
              {isRunningAll ? (
                <Button
                  variant="danger"
                  onClick={handleStop}
                  aria-label="Stop test"
                >
                  <Square className="w-3.5 h-3.5" />
                  Stop
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    trackEvent(
                      TRACKING_EVENTS.METRICS.TEST_METRIC_BUTTON_CLICKED,
                      { metric_id: metric?.id },
                    )
                    void handleRunAll()
                  }}
                  disabled={simulations.length === 0}
                  aria-label="Run test"
                >
                  <Play className="w-3.5 h-3.5" />
                  {Object.keys(testResults).length > 0 ? "Re-Test" : "Test"}
                </Button>
              )}
            </>
          )}
          <button
            onClick={handleClose}
            disabled={isRunningAll}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-surface-1 text-coval-text-muted hover:bg-surface-1 hover:text-coval-text transition-colors"
            aria-label="Close drawer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs (only show for existing metrics) */}
      {showTabs && !isNewMetric && (
        <div className="px-4 border-b border-coval-border">
          <Tabs
            value={viewMode}
            onValueChange={(value) => setViewMode(value as ViewMode)}
          >
            <TabsList>
              {tabOptions.map((tab) => (
                <TabsTrigger
                  key={tab.key}
                  value={tab.key}
                  disabled={isRunningAll}
                >
                  {tab.title}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      {scopeContent}

      {/* Metric filter section */}
      {metric?.id && (
        <MetricValueFilter
          isFloatMetric={isFloatMetric}
          filterValue={filterValue}
          filterOperator={filterOperator}
          stringOptions={stringValueOptions}
          onFilterValueChange={setFilterValue}
          onFilterOperatorChange={setFilterOperator}
        />
      )}

      {/* Import Section */}
      {allowImport && (
        <div className="px-4 py-3 border-b border-coval-border">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Input
                ref={importInputRef}
                placeholder="Enter simulation output ID"
                value={importId}
                onChange={(e) => {
                  setImportId(e.target.value)
                  setImportError(null)
                }}
                onKeyDown={handleImportKeyDown}
                className="font-mono text-sm h-8"
                startAdornment={
                  <Search className="w-3.5 h-3.5 text-coval-text-muted" />
                }
                error={!!importError}
                aria-label="Import simulation ID"
              />
              <Button
                variant="secondary"
                icon
                onClick={() => {
                  void handleImport()
                }}
                disabled={!importId.trim() || isImporting}
                tooltip="Import simulation"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {importError && (
              <span className="text-xs text-coval-danger-text">
                {importError}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Recent Transcripts Table */}
      <div className="flex-1 overflow-y-auto">
        {isLoadingSimulations ? (
          <div className="flex items-center justify-center py-12">
            <Skeleton className="h-4 w-16 rounded-md" />
          </div>
        ) : isSimulationsError && simulations.length === 0 ? (
          <div className="flex items-center justify-center px-4 py-12 text-coval-danger-text text-sm text-center">
            Failed to load recent{" "}
            {monitoringFilter
              ? "live conversations"
              : "simulated conversations"}
            . Please try again.
          </div>
        ) : simulations.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-coval-text-muted text-sm">
            No{" "}
            {monitoringFilter
              ? "live conversations"
              : "simulated conversations"}{" "}
            found{filterValue ? " matching the filter" : ""}.
          </div>
        ) : (
          <table className="w-full text-left table-fixed">
            <thead>
              <tr className="border-b border-coval-border">
                <th className="px-4 py-3 text-sm font-medium text-coval-text w-1/4">
                  Sim ID
                </th>
                <th className="px-4 py-3 text-sm font-medium text-coval-text w-1/5">
                  Date
                </th>
                {metric?.id && (
                  <th className="px-4 py-3 text-sm font-medium text-coval-text w-1/5">
                    AI Review
                  </th>
                )}
                {canRunEvaluations ? (
                  <th className="px-4 py-3 text-sm font-medium text-coval-text">
                    <div className="flex items-center justify-between">
                      <span>Test Result</span>
                      {!isRunningAll && passRate !== null && (
                        <span className="text-coval-warning-text flex items-center gap-0.5">
                          {passRate}%
                        </span>
                      )}
                    </div>
                  </th>
                ) : (
                  <th />
                )}
              </tr>
            </thead>
            <tbody>
              {simulations.map((simulation) => (
                <RecentSimulationRow
                  key={simulation.id}
                  isActive={isActiveItem(simulation.id)}
                  simulation={simulation}
                  testResult={testResults[simulation.id] || null}
                  isEvaluating={evaluatingIds.has(simulation.id)}
                  onRowClick={() => handleRowClick(simulation.id)}
                  onPointerEnter={() => handlePointerEnter(simulation.id)}
                  onPointerLeave={() => handlePointerLeave(simulation.id)}
                  onDelete={() => handleDelete(simulation.id)}
                  metricLabel={metric?.id ? metricDisplayName : undefined}
                  showTestResult={canRunEvaluations}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
